import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import dayjs from 'dayjs'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { GENDER_OPTIONS } from 'fhir/Person/constants'
import { PatFormData } from 'components/Assessments/PatAssessment/types/PatFormData'
import { PatAssessmentQuestionnaireItem } from 'components/Assessments/PatAssessment/types/questionnaire'
import { PatientBackgroundFormField } from 'components/Assessments/PatAssessment/sections/PatientBackground/formField'

type GenderCoding = { code: string; display: string } | string

// Build gender display map from GENDER_OPTIONS constant
const GENDER_DISPLAY_MAP: Record<string, string> = GENDER_OPTIONS.reduce<Record<string, string>>(
  (acc, option) => {
    acc[option.value.toLowerCase()] = option.label
    return acc
  },
  {}
)

/**
 * Patient Verification subsection adapter
 * Handles patient verification fields (legal name, preferred name, address, DOB, gender, medicaid ID)
 */
export class PatientVerificationAdapter extends BaseSubsectionAdapter<PatFormData> {
  /**
   * Extract gender code from valueCoding object or string
   */
  private extractGenderCode(value: unknown): string | undefined {
    if (value && typeof value === 'object' && 'code' in value) {
      return (value as { code: string }).code
    }
    return typeof value === 'string' ? value : undefined
  }

  /**
   * Build gender Coding object from string code
   */
  private buildGenderCoding(genderValue: string): GenderCoding {
    return {
      code: genderValue,
      display: GENDER_DISPLAY_MAP[genderValue.toLowerCase()] || genderValue
    }
  }

  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<PatFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const legalName = getAnswerValuesFromItems(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_LEGAL_NAME
    )?.at(0)

    const preferredNameValue = getAnswerValuesFromItems(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_PREFERRED_NAME
    )?.at(0)

    const preferredName =
      !preferredNameValue || preferredNameValue.trim() === '' ? null : preferredNameValue

    const address = getAnswerValuesFromItems(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_ADDRESS
    )?.at(0)

    const dob = getAnswerValuesFromItems(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_DOB
    )?.at(0)

    const genderValueCoding = getAnswerValuesFromItems(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_GENDER,
      true
    )?.at(0)

    const gender = this.extractGenderCode(genderValueCoding)

    const medicaidId = getAnswerValuesFromItems(
      flatAnswersArray,
      PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_MEDICAID_ID
    )?.at(0)

    const dateOfBirth = dob ? dayjs(dob) : undefined

    return {
      [PatientBackgroundFormField.LEGAL_NAME]: legalName,
      [PatientBackgroundFormField.PREFERRED_NAME]: preferredName,
      [PatientBackgroundFormField.ADDRESS]: address,
      [PatientBackgroundFormField.DATE_OF_BIRTH]: dateOfBirth,
      [PatientBackgroundFormField.GENDER]: gender,
      [PatientBackgroundFormField.MEDICAID_ID]: medicaidId
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(PatientBackgroundFormField.LEGAL_NAME)) {
      const legalName = this.getFormDataField(PatientBackgroundFormField.LEGAL_NAME)
      mutations.push({
        linkId: PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_LEGAL_NAME,
        value: legalName
      })
    }

    const preferredName = this.formDataFieldExists(PatientBackgroundFormField.PREFERRED_NAME)
      ? this.getFormDataField(PatientBackgroundFormField.PREFERRED_NAME)
      : undefined
    const preferredNameValue =
      preferredName != null && preferredName !== '' && preferredName.trim() !== ''
        ? preferredName
        : undefined
    mutations.push({
      linkId: PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_PREFERRED_NAME,
      value: preferredNameValue
    })

    if (this.formDataFieldExists(PatientBackgroundFormField.ADDRESS)) {
      const address = this.getFormDataField(PatientBackgroundFormField.ADDRESS)
      mutations.push({
        linkId: PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_ADDRESS,
        value: address
      })
    }

    const dob = this.formDataFieldExists(PatientBackgroundFormField.DATE_OF_BIRTH)
      ? this.getFormDataField(PatientBackgroundFormField.DATE_OF_BIRTH)
      : undefined
    const dobDayjs = dob ? dayjs(dob) : null
    const dobValue = dobDayjs && dobDayjs.isValid() ? dobDayjs.format('YYYY-MM-DD') : undefined
    mutations.push({
      linkId: PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_DOB,
      value: dobValue
    })

    const genderValue = this.formDataFieldExists(PatientBackgroundFormField.GENDER)
      ? this.getFormDataField(PatientBackgroundFormField.GENDER)
      : undefined
    const genderMutationValue =
      genderValue !== undefined && genderValue !== null
        ? typeof genderValue === 'string'
          ? this.buildGenderCoding(genderValue)
          : genderValue
        : undefined
    mutations.push({
      linkId: PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_GENDER,
      value: genderMutationValue
    })

    if (this.formDataFieldExists(PatientBackgroundFormField.MEDICAID_ID)) {
      const medicaidId = this.getFormDataField(PatientBackgroundFormField.MEDICAID_ID)
      mutations.push({
        linkId: PatAssessmentQuestionnaireItem.PATIENT_BACKGROUND_PATIENT_VERIFICATION_MEDICAID_ID,
        value: medicaidId
      })
    }

    return mutations
  }
}
